/** Course schedule (STORY-047): period + cadence per kind + skips → editable lesson slots.
 * Regenerating over existing slots asks for confirmation (the server refuses without it). */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type CSSProperties } from "react";
import { useTranslation } from "react-i18next";

import { ApiError } from "../api/client";
import type { CadenceEntry, Lesson, ScheduleIn, Skip } from "../api/types";
import { addLesson, deleteLesson, getSchedule, icsUrl, patchLesson, putSchedule } from "./api";
import { Button, useConfirm } from "../ui";

const KINDS = ["lecture", "lesson", "workshop", "lab", "other"] as const;
type Form = Omit<ScheduleIn, "cadence" | "skips"> & { cadence: CadenceEntry[]; skips: Skip[] };
const PERIODS = ["week", "month", "semester", "year", "custom"] as const;
/* Two rows, two ranks (2026-09-09). The build form is a form the teacher fills in once, so
   its controls sit on the default step and line up with the submit button beside them; the
   lesson list is a dense table of slots, which is exactly what the compact step is for. */
const formField = "field";
const rowField = "field field-sm";

function addDays(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

function presetEnd(period: string, start: string): string | null {
  switch (period) {
    case "week": return addDays(start, 6);
    case "month": return addDays(start, 29);
    case "semester": return addDays(start, 16 * 7 - 1);
    case "year": return addDays(start, 39 * 7 - 1);
    default: return null;
  }
}

export function SchedulePanel({ courseId, onLessonCount }: { courseId: string; onLessonCount?: (n: number) => void }) {
  const { t, i18n } = useTranslation();
  const confirm = useConfirm();
  const qc = useQueryClient();
  const schedule = useQuery({ queryKey: ["schedule", courseId], queryFn: () => getSchedule(courseId), retry: false });
  const has = !!schedule.data;
  const [open, setOpen] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState<Form>({ period_type: "semester", start_date: today, end_date: presetEnd("semester", today) ?? today, cadence: [{ kind: "lecture", weekday: 0, time: "09:00" }], skips: [], confirm: false });
  const [msg, setMsg] = useState<string | null>(null);
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["schedule", courseId] });
  };
  const save = useMutation({
    mutationFn: (confirm: boolean) => putSchedule(courseId, { ...form, confirm }),
    onSuccess: (s) => { setMsg(null); setOpen(false); onLessonCount?.(s.lessons.length); refresh(); },
    onError: async (e) => {
      if (e instanceof ApiError && e.code === "regenerate_confirm") {
        const n = (e.body as { lessons?: number })?.lessons ?? 0;
        if (await confirm({ title: t("schedule.regenerateTitle", { n }), body: t("schedule.regenerateConfirm"), confirmLabel: t("schedule.rebuild"), destructive: true })) save.mutate(true);
        return;
      }
      setMsg(e instanceof ApiError ? e.detail : String(e));
    },
  });
  const weekdays = Array.from({ length: 7 }, (_, i) => new Date(Date.UTC(2024, 0, 1 + i)).toLocaleDateString(i18n.language, { weekday: "short", timeZone: "UTC" }));
  const fmtDate = (iso: string) => new Date(`${iso}T00:00:00Z`).toLocaleDateString(i18n.language, { weekday: "short", day: "numeric", month: "short", timeZone: "UTC" });

  function setPeriod(period_type: string) {
    const end = presetEnd(period_type, form.start_date);
    setForm({ ...form, period_type, end_date: end ?? form.end_date });
  }
  function setCadence(i: number, patch: Partial<CadenceEntry>) {
    setForm({ ...form, cadence: form.cadence.map((c, j) => (j === i ? { ...c, ...patch } : c)) });
  }
  function setSkip(i: number, patch: Partial<Skip>) {
    setForm({ ...form, skips: form.skips.map((s, j) => (j === i ? { ...s, ...patch } : s)) });
  }

  const lessons = schedule.data?.lessons ?? [];
  const summary = has
    ? t("schedule.summary", { n: lessons.length, from: fmtDate(schedule.data!.start_date), to: fmtDate(schedule.data!.end_date) })
    : t("schedule.none");

  return (
    <section className="flex flex-col gap-3 rounded-[var(--radius-card)] bg-[color:var(--surface)] p-4 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-semibold">{t("schedule.title")}</h2>
        <div className="flex items-center gap-3 text-sm">
          <span className="text-[color:var(--ink-2)]">{summary}</span>
          {has && <a className="underline" href={icsUrl(courseId)}>{t("schedule.ics")}</a>}
          <button type="button" className="underline" onClick={() => setOpen(!open)}>{open ? t("editor.cancel") : has ? t("schedule.rebuild") : t("schedule.build")}</button>
        </div>
      </div>

      {open && (
        <form className="flex flex-col gap-3" onSubmit={(e) => { e.preventDefault(); save.mutate(false); }}>
          <div className="flex flex-wrap items-end gap-3 text-sm">
            <label className="flex flex-col gap-1">
              {t("schedule.period")}
              <select className={formField} value={form.period_type} onChange={(e) => setPeriod(e.target.value)}>
                {PERIODS.map((p) => <option key={p} value={p}>{t(`schedule.periods.${p}`)}</option>)}
              </select>
            </label>
            <label className="flex flex-col gap-1">
              {t("schedule.from")}
              <input type="date" className={formField} value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value, end_date: presetEnd(form.period_type, e.target.value) ?? form.end_date })} />
            </label>
            <label className="flex flex-col gap-1">
              {t("schedule.to")}
              <input type="date" className={formField} value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value, period_type: "custom" })} />
            </label>
          </div>

          <div className="flex flex-col gap-2 text-sm">
            <span className="font-medium">{t("schedule.cadence")}</span>
            {form.cadence.map((c, i) => (
              <div key={i} className="flex flex-wrap items-center gap-2">
                <select className={formField} value={c.kind} onChange={(e) => setCadence(i, { kind: e.target.value })}>
                  {KINDS.map((k) => <option key={k} value={k}>{t(`schedule.kinds.${k}`)}</option>)}
                </select>
                <select className={formField} value={c.weekday} onChange={(e) => setCadence(i, { weekday: Number(e.target.value) })}>
                  {weekdays.map((w, wd) => <option key={wd} value={wd}>{w}</option>)}
                </select>
                <input type="time" className={formField} value={c.time ?? ""} onChange={(e) => setCadence(i, { time: e.target.value || null })} />
                <Button variant="tertiary" aria-label={t("plan.delete")} onClick={() => setForm({ ...form, cadence: form.cadence.filter((_, j) => j !== i) })}>×</Button>
              </div>
            ))}
            <button type="button" className="self-start underline" onClick={() => setForm({ ...form, cadence: [...form.cadence, { kind: "lesson", weekday: 1, time: null }] })}>+ {t("schedule.addCadence")}</button>
          </div>

          <div className="flex flex-col gap-2 text-sm">
            <span className="font-medium">{t("schedule.skips")}</span>
            {form.skips.map((s, i) => (
              <div key={i} className="flex flex-wrap items-center gap-2">
                <input type="date" className={formField} value={s.from} onChange={(e) => setSkip(i, { from: e.target.value })} />
                <input type="date" className={formField} value={s.to ?? ""} onChange={(e) => setSkip(i, { to: e.target.value || null })} />
                <input className={`${formField} min-w-0 flex-1`} placeholder={t("schedule.reason")} value={s.reason ?? ""} onChange={(e) => setSkip(i, { reason: e.target.value })} />
                <Button variant="tertiary" aria-label={t("plan.delete")} onClick={() => setForm({ ...form, skips: form.skips.filter((_, j) => j !== i) })}>×</Button>
              </div>
            ))}
            <button type="button" className="self-start underline" onClick={() => setForm({ ...form, skips: [...form.skips, { from: form.start_date, to: null, reason: "" }] })}>+ {t("schedule.addSkip")}</button>
          </div>

          {msg && <p role="alert" className="text-sm text-[color:var(--danger)]">{msg}</p>}
          <Button type="submit" loading={save.isPending} disabled={form.cadence.length === 0}>{has ? t("schedule.rebuild") : t("schedule.build")}</Button>
        </form>
      )}

      {has && !open && <LessonList courseId={courseId} lessons={lessons} onChange={refresh} fmtDate={fmtDate} />}
    </section>
  );
}

function LessonList({ courseId, lessons, onChange, fmtDate }: { courseId: string; lessons: Lesson[]; onChange: () => void; fmtDate: (iso: string) => string }) {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ date: lessons[0]?.date ?? "", start_time: "", kind: "lesson", title: "" });
  const patch = useMutation({ mutationFn: ({ id, body }: { id: string; body: Parameters<typeof patchLesson>[1] }) => patchLesson(id, body), onSuccess: onChange });
  const remove = useMutation({ mutationFn: deleteLesson, onSuccess: onChange });
  const add = useMutation({
    mutationFn: () => addLesson(courseId, { date: draft.date, start_time: draft.start_time || null, kind: draft.kind, title: draft.title }),
    onSuccess: () => { setAdding(false); onChange(); },
  });
  return (
    <div className="flex flex-col gap-1 text-sm">
      <ol className="grid-fit" style={{ "--fit-min": "44rem", gap: "0.25rem 1.5rem" } as CSSProperties}>
        {lessons.map((lo) => (
          <li key={lo.lesson_id} className="flex flex-wrap items-center gap-2 rounded-[var(--radius-control)] px-2 py-1 hover:bg-[color:var(--bg)]">
            <span className="w-8 font-mono text-xs text-[color:var(--ink-2)]">{lo.position + 1}</span>
            <input type="date" className={rowField} value={lo.date} onChange={(e) => patch.mutate({ id: lo.lesson_id, body: { date: e.target.value } })} />
            <input type="time" className={rowField} value={lo.start_time ?? ""} onChange={(e) => patch.mutate({ id: lo.lesson_id, body: { start_time: e.target.value || undefined } })} />
            <select className={rowField} value={lo.kind} onChange={(e) => patch.mutate({ id: lo.lesson_id, body: { kind: e.target.value } })}>
              {KINDS.map((k) => <option key={k} value={k}>{t(`schedule.kinds.${k}`)}</option>)}
            </select>
            <input className={`${rowField} min-w-40 flex-1`} placeholder={t("schedule.titlePlaceholder")} defaultValue={lo.title} onBlur={(e) => { if (e.target.value !== lo.title) patch.mutate({ id: lo.lesson_id, body: { title: e.target.value } }); }} />
            <span className="text-xs text-[color:var(--ink-2)]">{fmtDate(lo.date)}</span>
            <Button
              variant="tertiary"
              size="sm"
              aria-label={t("plan.delete")}
              onClick={async () => {
                if (await confirm({ title: t("schedule.deleteTitle"), body: t("schedule.deleteConfirm"), confirmLabel: t("plan.delete"), destructive: true })) remove.mutate(lo.lesson_id);
              }}
            >
              ×
            </Button>
          </li>
        ))}
      </ol>
      {adding ? (
        <form className="flex flex-wrap items-center gap-2" onSubmit={(e) => { e.preventDefault(); add.mutate(); }}>
          <input type="date" className={rowField} value={draft.date} onChange={(e) => setDraft({ ...draft, date: e.target.value })} required />
          <input type="time" className={rowField} value={draft.start_time} onChange={(e) => setDraft({ ...draft, start_time: e.target.value })} />
          <select className={rowField} value={draft.kind} onChange={(e) => setDraft({ ...draft, kind: e.target.value })}>
            {KINDS.map((k) => <option key={k} value={k}>{t(`schedule.kinds.${k}`)}</option>)}
          </select>
          <input className={`${rowField} min-w-40 flex-1`} placeholder={t("schedule.titlePlaceholder")} value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} />
          <Button variant="secondary" size="sm" type="submit" loading={add.isPending}>{t("schedule.addSlot")}</Button>
          <button type="button" className="underline" onClick={() => setAdding(false)}>{t("editor.cancel")}</button>
        </form>
      ) : (
        <button type="button" className="self-start underline" onClick={() => setAdding(true)}>+ {t("schedule.addSlot")}</button>
      )}
    </div>
  );
}
